import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Select, TreeSelect, message } from 'antd';
import api from '../services/api';

const { Option } = Select;
const { SHOW_CHILD } = TreeSelect;

interface Subject {
  id: string;
  _id: string;
  name: string;
  displayName?: string;
  type: 'ACADEMIC' | 'COMPETITIVE' | 'ACTIVITY';
  category?: string;
  class?: string;
  stream?: string;
  exam?: string;
  activityType?: string;
  parentCategory?: string;
}

interface SubjectSelectorProps {
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  mode?: 'single' | 'multiple';
  placeholder?: string;
  showHierarchy?: boolean;
  filterType?: 'all' | 'ACADEMIC' | 'COMPETITIVE' | 'ACTIVITY';
  className?: string;
}

const SubjectSelector: React.FC<SubjectSelectorProps> = ({
  value,
  onChange,
  mode = 'single',
  placeholder = 'Select subjects',
  showHierarchy = false,
  filterType,
  className,
}) => {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [hierarchy, setHierarchy] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState<'ACADEMIC' | 'COMPETITIVE' | 'ACTIVITY' | ''>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [selectedStream, setSelectedStream] = useState<string>('');
  const [selectedExam, setSelectedExam] = useState<string>('');
  const [selectedActivityType, setSelectedActivityType] = useState<string>('');

  useEffect(() => {
    if (filterType && filterType !== 'all') {
      setSelectedType(filterType);
      setSelectedCategory('');
      setSelectedClass('');
      setSelectedStream('');
      setSelectedExam('');
      setSelectedActivityType('');
      return;
    }
    setSelectedType('');
  }, [filterType]);

  const fetchSubjects = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/subjects/hierarchy');
      setHierarchy(response.data);
      setSubjects(flattenHierarchy(response.data));
    } catch (error) {
      console.error('Error fetching subjects:', error);
      message.error('Failed to load subjects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  const flattenHierarchy = (hierarchy: any): Subject[] => {
    const flat: Subject[] = [];
    
    Object.entries(hierarchy).forEach(([type, categories]: [string, any]) => {
      Object.entries(categories).forEach(([category, items]: [string, any]) => {
        Object.entries(items).forEach(([key, subjects]: [string, any]) => {
          subjects.forEach((subject: any) => {
            // Only add subjects with valid IDs and map _id to id
            if (subject && subject._id && subject._id !== null) {
              flat.push({
                ...subject,
                id: subject._id // Map _id to id for consistency
              });
            }
          });
        });
      });
    });
    
    return flat;
  };

  const buildTreeData = (hierarchy: any) => {
    const treeData: any[] = [];
    
    Object.entries(hierarchy).forEach(([type, categories]: [string, any]) => {
      const typeNode: any = {
        title: type.charAt(0) + type.slice(1).toLowerCase(),
        value: type,
        key: type,
        children: [],
      };
      
      Object.entries(categories).forEach(([category, items]: [string, any]) => {
        const categoryNode: any = {
          title: category,
          value: `${type}-${category}`,
          key: `${type}-${category}`,
          children: [],
        };
        
        Object.entries(items).forEach(([key, subjects]: [string, any]) => {
          const keyNode: any = {
            title: key,
            value: `${type}-${category}-${key}`,
            key: `${type}-${category}-${key}`,
            children: [],
          };
          
          subjects.forEach((subject: Subject) => {
            if (subject && subject.id && subject.id !== null) {
              keyNode.children.push({
                title: subject.displayName || subject.name,
                value: subject.id,
                key: subject.id,
                isLeaf: true,
              });
            }
          });
          
          categoryNode.children.push(keyNode);
        });
        
        typeNode.children.push(categoryNode);
      });
      
      treeData.push(typeNode);
    });
    
    return treeData;
  };

  const handleCategoryChange = (category?: string) => {
    setSelectedCategory(category || '');
    setSelectedClass('');
    setSelectedStream('');
    setSelectedExam('');
    setSelectedActivityType('');
  };

  const handleClassChange = (classLevel?: string) => {
    setSelectedClass(classLevel || '');
    setSelectedStream('');
  };

  const effectiveType = useMemo(
    () => (filterType && filterType !== 'all' ? filterType : selectedType || undefined),
    [filterType, selectedType],
  );

  const subjectsWithIds = useMemo(
    () => subjects.filter((subject) => subject && subject.id && subject.id !== null),
    [subjects],
  );

  const subjectsByType = useMemo(
    () => (effectiveType ? subjectsWithIds.filter((subject) => subject.type === effectiveType) : subjectsWithIds),
    [subjectsWithIds, effectiveType],
  );

  const categoryOptions = useMemo(
    () => Array.from(new Set(subjectsByType.map((subject) => subject.category).filter(Boolean) as string[])),
    [subjectsByType],
  );

  const subjectsByTypeAndCategory = useMemo(
    () =>
      selectedCategory
        ? subjectsByType.filter((subject) => subject.category === selectedCategory)
        : subjectsByType,
    [subjectsByType, selectedCategory],
  );

  const classOptions = useMemo(
    () => Array.from(new Set(subjectsByTypeAndCategory.map((subject) => subject.class).filter(Boolean) as string[])),
    [subjectsByTypeAndCategory],
  );

  const streamOptions = useMemo(
    () =>
      Array.from(
        new Set(
          subjectsByTypeAndCategory
            .filter((subject) => !selectedClass || subject.class === selectedClass)
            .map((subject) => subject.stream)
            .filter(Boolean) as string[],
        ),
      ),
    [subjectsByTypeAndCategory, selectedClass],
  );

  const examOptions = useMemo(
    () =>
      Array.from(new Set(subjectsByTypeAndCategory.map((subject) => subject.exam).filter(Boolean) as string[])),
    [subjectsByTypeAndCategory],
  );

  const activityTypeOptions = useMemo(
    () =>
      Array.from(
        new Set(subjectsByTypeAndCategory.map((subject) => subject.activityType).filter(Boolean) as string[]),
      ),
    [subjectsByTypeAndCategory],
  );

  const filteredSubjects = useMemo(
    () =>
      subjectsByTypeAndCategory.filter((subject) => {
        if (selectedClass && subject.class !== selectedClass) return false;
        if (selectedStream && subject.stream !== selectedStream) return false;
        if (selectedExam && subject.exam !== selectedExam) return false;
        if (selectedActivityType && subject.activityType !== selectedActivityType) return false;
        return true;
      }),
    [subjectsByTypeAndCategory, selectedClass, selectedStream, selectedExam, selectedActivityType],
  );

  useEffect(() => {
    if (selectedCategory && !categoryOptions.includes(selectedCategory)) {
      setSelectedCategory('');
      setSelectedClass('');
      setSelectedStream('');
      setSelectedExam('');
      setSelectedActivityType('');
    }
  }, [selectedCategory, categoryOptions]);

  useEffect(() => {
    if (selectedClass && !classOptions.includes(selectedClass)) {
      setSelectedClass('');
      setSelectedStream('');
    }
  }, [selectedClass, classOptions]);

  useEffect(() => {
    if (selectedStream && !streamOptions.includes(selectedStream)) {
      setSelectedStream('');
    }
  }, [selectedStream, streamOptions]);

  useEffect(() => {
    if (selectedExam && !examOptions.includes(selectedExam)) {
      setSelectedExam('');
    }
  }, [selectedExam, examOptions]);

  useEffect(() => {
    if (selectedActivityType && !activityTypeOptions.includes(selectedActivityType)) {
      setSelectedActivityType('');
    }
  }, [selectedActivityType, activityTypeOptions]);

  useEffect(() => {
    if (!onChange) return;
    if (loading) return;
    if (subjectsWithIds.length === 0) return;
    const allowedIds = new Set(filteredSubjects.map((subject) => subject.id));
    if (mode === 'multiple') {
      const selectedValues = Array.isArray(value) ? value : value ? [value] : [];
      const nextValues = selectedValues.filter((id) => allowedIds.has(id));
      if (
        selectedValues.length !== nextValues.length ||
        selectedValues.some((item, index) => item !== nextValues[index])
      ) {
        onChange(nextValues);
      }
      return;
    }
    const selectedValue = Array.isArray(value) ? value[0] : value;
    if (selectedValue && !allowedIds.has(selectedValue)) {
      onChange('');
    }
  }, [filteredSubjects, mode, onChange, value, loading, subjectsWithIds.length]);

  if (showHierarchy) {
    return (
      <div className={className}>
        <TreeSelect
          value={value}
          onChange={onChange}
          treeData={buildTreeData(hierarchy)}
          placeholder={placeholder}
          treeCheckable={mode === 'multiple'}
          showCheckedStrategy={SHOW_CHILD}
          style={{ width: '100%' }}
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          loading={loading}
        />
      </div>
    );
  }

  return (
    <div className={className}>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
        {!filterType && (
          <Select
            value={selectedType || undefined}
            onChange={(val) => {
              setSelectedType((val || '') as any);
              setSelectedCategory('');
              setSelectedClass('');
              setSelectedStream('');
              setSelectedExam('');
              setSelectedActivityType('');
            }}
            placeholder="Subject Type"
            style={{ width: 150 }}
            allowClear
          >
            <Option value="ACADEMIC">Academic</Option>
            <Option value="COMPETITIVE">Competitive</Option>
            <Option value="ACTIVITY">Activity</Option>
          </Select>
        )}
        
        <Select
          value={selectedCategory || undefined}
          onChange={handleCategoryChange}
          placeholder="Category"
          style={{ width: 180 }}
          allowClear
        >
          {categoryOptions.map(cat => (
            <Option key={cat} value={cat}>{cat}</Option>
          ))}
        </Select>
        
        {streamOptions.length > 0 && (
          <Select
            value={selectedStream || undefined}
            onChange={(val) => setSelectedStream(val || '')}
            placeholder="Stream"
            style={{ width: 120 }}
            allowClear
          >
            {streamOptions.map((stream) => (
              <Option key={stream} value={stream}>
                {stream}
              </Option>
            ))}
          </Select>
        )}
        
        {examOptions.length > 0 && (
          <Select
            value={selectedExam || undefined}
            onChange={(val) => setSelectedExam(val || '')}
            placeholder="Exam"
            style={{ width: 120 }}
            allowClear
          >
            {examOptions.map((exam) => (
              <Option key={exam} value={exam}>
                {exam}
              </Option>
            ))}
          </Select>
        )}
        
        {activityTypeOptions.length > 0 && (
          <Select
            value={selectedActivityType || undefined}
            onChange={(val) => setSelectedActivityType(val || '')}
            placeholder="Activity Type"
            style={{ width: 150 }}
            allowClear
          >
            {activityTypeOptions.map((activityType) => (
              <Option key={activityType} value={activityType}>
                {activityType}
              </Option>
            ))}
          </Select>
        )}
        
        {classOptions.length > 0 && (
          <Select
            value={selectedClass || undefined}
            onChange={handleClassChange}
            placeholder="Class"
            style={{ width: 120 }}
            allowClear
          >
            {classOptions.map(cls => (
                <Option key={cls} value={cls}>
                  {cls}
                </Option>
              ))}
          </Select>
        )}
      </div>
      
      <Select
        value={value}
        onChange={onChange}
        mode={mode === 'multiple' ? 'multiple' : undefined}
        placeholder={placeholder}
        style={{ width: '100%' }}
        loading={loading}
        showSearch
        filterOption={(input, option) =>
          String(option?.children).toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
      >
        {filteredSubjects
          .filter(subject => subject.id && subject.id !== null)
          .map(subject => (
          <Option key={subject.id} value={subject.id}>
            {subject.displayName || subject.name}
            {subject.class && ` (${subject.class})`}
            {subject.stream && ` - ${subject.stream}`}
            {subject.exam && ` - ${subject.exam}`}
            {subject.activityType && ` - ${subject.activityType}`}
          </Option>
        ))}
      </Select>
    </div>
  );
};

export default SubjectSelector;
